'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';

export type ThemeId =
  | 'theme-white-midnight' |'theme-tech'| 'theme-emerald' | 'theme-crimson' | 'theme-gold' | 'theme-aurora' |'theme-ocean' | 'theme-midnight' | 'theme-rose' | 'theme-forest' | 'theme-solar';

export type FontId = 'font-jakarta' | 'font-dm' | 'font-manrope' | 'font-cabinet' | 'font-mono';

export interface StudentRequest {
  id: string;
  studentId: string;
  studentName: string;
  course: string;
  subject: string;
  message: string;
  timestamp: string;
  read: boolean;
  status: 'pending' | 'resolved';
}

interface AppContextType {
  theme: ThemeId;
  setTheme: (t: ThemeId) => void;
  font: FontId;
  setFont: (f: FontId) => void;
  userRole: 'admin' | 'staff' | 'student' | null;
  setUserRole: (r: 'admin' | 'staff' | 'student' | null) => void;
  studentRequests: StudentRequest[];
  addStudentRequest: (req: Omit<StudentRequest, 'id' | 'timestamp' | 'read' | 'status'>) => void;
  markRequestRead: (id: string) => void;
  resolveRequest: (id: string) => void;
  unreadRequestCount: number;
  adminThemes: ThemeId[];
  setAdminThemes: (themes: ThemeId[]) => void;
}

const AppContext = createContext<AppContextType>({
  theme: 'theme-white-midnight',
  setTheme: () => {},
  font: 'font-jakarta',
  setFont: () => {},
  userRole: null,
  setUserRole: () => {},
  studentRequests: [],
  addStudentRequest: () => {},
  markRequestRead: () => {},
  resolveRequest: () => {},
  unreadRequestCount: 0,
  adminThemes: ['theme-tech', 'theme-emerald', 'theme-crimson', 'theme-aurora', 'theme-ocean'],
  setAdminThemes: () => {},
});

export const THEMES: { id: ThemeId; name: string; primary: string; accent: string; bg: string; preview: string; number: number; isDefault?: boolean; isLight?: boolean }[] = [
  { number: 0, id: 'theme-white-midnight', name: 'White & Midnight', primary: '#1E3A5F', accent: '#2563EB', bg: '#FFFFFF', preview: 'linear-gradient(135deg, #1E3A5F, #2563EB)', isDefault: true, isLight: true },
  { number: 1, id: 'theme-tech', name: 'Tech Dark', primary: '#6C63FF', accent: '#00D4FF', bg: '#0A0E1A', preview: 'linear-gradient(135deg, #6C63FF, #00D4FF)' },
  { number: 2, id: 'theme-emerald', name: 'Emerald', primary: '#10B981', accent: '#34D399', bg: '#0A1A12', preview: 'linear-gradient(135deg, #10B981, #34D399)' },
  { number: 3, id: 'theme-crimson', name: 'Crimson', primary: '#EF4444', accent: '#F97316', bg: '#1A0A0A', preview: 'linear-gradient(135deg, #EF4444, #F97316)' },
  { number: 4, id: 'theme-gold', name: 'Gold Rush', primary: '#F59E0B', accent: '#FBBF24', bg: '#1A1400', preview: 'linear-gradient(135deg, #F59E0B, #FBBF24)' },
  { number: 5, id: 'theme-aurora', name: 'Aurora', primary: '#8B5CF6', accent: '#EC4899', bg: '#0F0A1A', preview: 'linear-gradient(135deg, #8B5CF6, #EC4899)' },
  { number: 6, id: 'theme-ocean', name: 'Deep Ocean', primary: '#0EA5E9', accent: '#38BDF8', bg: '#020C1B', preview: 'linear-gradient(135deg, #0EA5E9, #38BDF8)' },
  { number: 7, id: 'theme-midnight', name: 'Midnight', primary: '#6366F1', accent: '#A78BFA', bg: '#0D0D1A', preview: 'linear-gradient(135deg, #6366F1, #A78BFA)' },
  { number: 8, id: 'theme-rose', name: 'Rose Gold', primary: '#F43F5E', accent: '#FB7185', bg: '#1A0A10', preview: 'linear-gradient(135deg, #F43F5E, #FB7185)' },
  { number: 9, id: 'theme-forest', name: 'Forest', primary: '#22C55E', accent: '#86EFAC', bg: '#071A0E', preview: 'linear-gradient(135deg, #22C55E, #86EFAC)' },
  { number: 10, id: 'theme-solar', name: 'Solar Flare', primary: '#FB923C', accent: '#FDE68A', bg: '#1A0E00', preview: 'linear-gradient(135deg, #FB923C, #FDE68A)' },
];

export const FONTS: { id: FontId; name: string; family: string }[] = [
  { id: 'font-jakarta', name: 'Plus Jakarta', family: "'Plus Jakarta Sans', sans-serif" },
  { id: 'font-dm', name: 'DM Sans', family: "'DM Sans', sans-serif" },
  { id: 'font-manrope', name: 'Manrope', family: "'Manrope', sans-serif" },
  { id: 'font-cabinet', name: 'Cabinet Grotesk', family: "'Cabinet Grotesk', sans-serif" },
  { id: 'font-mono', name: 'JetBrains Mono', family: "'JetBrains Mono', monospace" },
];

const THEME_VARS: Record<ThemeId, Record<string, string>> = {
  'theme-white-midnight': {
    '--primary': '#1E3A5F',
    '--accent': '#2563EB',
    '--background': '#FFFFFF',
    '--card': '#F8FAFC',
    '--secondary': '#EFF6FF',
    '--border': '#DBEAFE',
    '--input': '#F1F5F9',
    '--muted': '#F0F4F8',
    '--foreground': '#0F172A',
    '--card-foreground': '#0F172A',
    '--secondary-foreground': '#334155',
    '--muted-foreground': '#64748B',
    '--primary-foreground': '#FFFFFF',
    '--accent-foreground': '#FFFFFF',
  },
  'theme-tech': { '--primary': '#6C63FF', '--accent': '#00D4FF', '--background': '#0A0E1A', '--card': '#111827', '--secondary': '#1A1F35', '--border': '#1E2D4A', '--input': '#1A2035', '--muted': '#1E2438', '--foreground': '#E8EAFF', '--card-foreground': '#E8EAFF', '--secondary-foreground': '#A0A8D0', '--muted-foreground': '#6B7280', '--primary-foreground': '#FFFFFF', '--accent-foreground': '#0A0E1A' },
  'theme-emerald': { '--primary': '#10B981', '--accent': '#34D399', '--background': '#0A1A12', '--card': '#0F2018', '--secondary': '#1A2E20', '--border': '#1E3A28', '--input': '#162A1E', '--muted': '#1A2E22', '--foreground': '#E8FFF4', '--card-foreground': '#E8FFF4', '--secondary-foreground': '#A0D0B8', '--muted-foreground': '#6B8070', '--primary-foreground': '#FFFFFF', '--accent-foreground': '#0A1A12' },
  'theme-crimson': { '--primary': '#EF4444', '--accent': '#F97316', '--background': '#1A0A0A', '--card': '#1F1010', '--secondary': '#2A1515', '--border': '#3A1E1E', '--input': '#2A1515', '--muted': '#2A1818', '--foreground': '#FFE8E8', '--card-foreground': '#FFE8E8', '--secondary-foreground': '#D0A0A0', '--muted-foreground': '#806B6B', '--primary-foreground': '#FFFFFF', '--accent-foreground': '#1A0A0A' },
  'theme-gold': { '--primary': '#F59E0B', '--accent': '#FBBF24', '--background': '#1A1400', '--card': '#1F1800', '--secondary': '#2A2200', '--border': '#3A3000', '--input': '#2A2200', '--muted': '#2A2400', '--foreground': '#FFF8E8', '--card-foreground': '#FFF8E8', '--secondary-foreground': '#D0C0A0', '--muted-foreground': '#80706B', '--primary-foreground': '#1A1400', '--accent-foreground': '#1A1400' },
  'theme-aurora': { '--primary': '#8B5CF6', '--accent': '#EC4899', '--background': '#0F0A1A', '--card': '#130F1F', '--secondary': '#1E1530', '--border': '#2A1E40', '--input': '#1A1230', '--muted': '#1E1535', '--foreground': '#F0E8FF', '--card-foreground': '#F0E8FF', '--secondary-foreground': '#B0A0D0', '--muted-foreground': '#706B80', '--primary-foreground': '#FFFFFF', '--accent-foreground': '#FFFFFF' },
  'theme-ocean': { '--primary': '#0EA5E9', '--accent': '#38BDF8', '--background': '#020C1B', '--card': '#071525', '--secondary': '#0C1E30', '--border': '#0E2840', '--input': '#0A1C2E', '--muted': '#0C2035', '--foreground': '#E8F8FF', '--card-foreground': '#E8F8FF', '--secondary-foreground': '#A0C8D0', '--muted-foreground': '#6B7880', '--primary-foreground': '#FFFFFF', '--accent-foreground': '#020C1B' },
  'theme-midnight': { '--primary': '#6366F1', '--accent': '#A78BFA', '--background': '#0D0D1A', '--card': '#111120', '--secondary': '#181828', '--border': '#222238', '--input': '#161628', '--muted': '#1A1A30', '--foreground': '#E8E8FF', '--card-foreground': '#E8E8FF', '--secondary-foreground': '#A0A0D0', '--muted-foreground': '#6B6B80', '--primary-foreground': '#FFFFFF', '--accent-foreground': '#FFFFFF' },
  'theme-rose': { '--primary': '#F43F5E', '--accent': '#FB7185', '--background': '#1A0A10', '--card': '#200F15', '--secondary': '#2A1520', '--border': '#3A1E28', '--input': '#2A1520', '--muted': '#2A1822', '--foreground': '#FFE8F0', '--card-foreground': '#FFE8F0', '--secondary-foreground': '#D0A0B0', '--muted-foreground': '#806B70', '--primary-foreground': '#FFFFFF', '--accent-foreground': '#FFFFFF' },
  'theme-forest': { '--primary': '#22C55E', '--accent': '#86EFAC', '--background': '#071A0E', '--card': '#0C2015', '--secondary': '#102A1A', '--border': '#163820', '--input': '#0E2818', '--muted': '#122A1C', '--foreground': '#E8FFE8', '--card-foreground': '#E8FFE8', '--secondary-foreground': '#A0D0A8', '--muted-foreground': '#6B806B', '--primary-foreground': '#071A0E', '--accent-foreground': '#071A0E' },
  'theme-solar': { '--primary': '#FB923C', '--accent': '#FDE68A', '--background': '#1A0E00', '--card': '#201200', '--secondary': '#2A1A00', '--border': '#3A2800', '--input': '#2A1800', '--muted': '#2A1C00', '--foreground': '#FFF0E8', '--card-foreground': '#FFF0E8', '--secondary-foreground': '#D0B8A0', '--muted-foreground': '#807060', '--primary-foreground': '#1A0E00', '--accent-foreground': '#1A0E00' },
};

const INITIAL_REQUESTS: StudentRequest[] = [
  { id: 'req-001', studentId: 'std-001', studentName: 'Rahul Sharma', course: 'ADCA', subject: 'Fee Receipt Request', message: 'Please provide the fee receipt for July installment payment made on 05/07/2026.', timestamp: '2026-08-08T10:30:00', read: false, status: 'pending' },
  { id: 'req-002', studentId: 'std-003', studentName: 'Anjali Verma', course: 'DIFA', subject: 'Attendance Correction', message: 'I was present on 02/08/2026 but it shows absent. Please check and correct.', timestamp: '2026-08-07T14:15:00', read: false, status: 'pending' },
];

// Default 5 admin-configurable themes (excluding the default white-midnight)
const DEFAULT_ADMIN_THEMES: ThemeId[] = ['theme-tech', 'theme-emerald', 'theme-crimson', 'theme-aurora', 'theme-ocean'];

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeId>('theme-white-midnight');
  const [font, setFontState] = useState<FontId>('font-jakarta');
  const [userRole, setUserRole] = useState<'admin' | 'staff' | 'student' | null>(null);
  const [studentRequests, setStudentRequests] = useState<StudentRequest[]>(INITIAL_REQUESTS);
  const [adminThemes, setAdminThemesState] = useState<ThemeId[]>(DEFAULT_ADMIN_THEMES);

  const setTheme = (t: ThemeId) => {
    setThemeState(t);
    if (typeof document !== 'undefined') {
      const vars = THEME_VARS[t];
      Object.entries(vars).forEach(([k, v]) => {
        document.documentElement.style.setProperty(k, v);
      });
      // Update foreground/card-foreground for light/dark
      const themeObj = THEMES.find(th => th.id === t);
      if (themeObj?.isLight) {
        document.documentElement.classList.add('theme-light');
        document.documentElement.classList.remove('theme-dark');
      } else {
        document.documentElement.classList.add('theme-dark');
        document.documentElement.classList.remove('theme-light');
      }
    }
  };

  const setFont = (f: FontId) => {
    setFontState(f);
    const fontObj = FONTS.find(fo => fo.id === f);
    if (fontObj && typeof document !== 'undefined') {
      document.documentElement.style.setProperty('--font-sans', fontObj.family);
      document.body.style.fontFamily = fontObj.family;
    }
  };

  const setAdminThemes = (themes: ThemeId[]) => {
    setAdminThemesState(themes.slice(0, 5));
  };

  // Apply default theme on mount
  useEffect(() => {
    setTheme('theme-white-midnight');
  }, []);

  const addStudentRequest = (req: Omit<StudentRequest, 'id' | 'timestamp' | 'read' | 'status'>) => {
    const newReq: StudentRequest = {
      ...req,
      id: `req-${Date.now()}`,
      timestamp: new Date().toISOString(),
      read: false,
      status: 'pending',
    };
    setStudentRequests(prev => [newReq, ...prev]);
  };

  const markRequestRead = (id: string) => {
    setStudentRequests(prev => prev.map(r => r.id === id ? { ...r, read: true } : r));
  };

  const resolveRequest = (id: string) => {
    setStudentRequests(prev => prev.map(r => r.id === id ? { ...r, status: 'resolved', read: true } : r));
  };

  const unreadRequestCount = studentRequests.filter(r => !r.read).length;

  return (
    <AppContext.Provider value={{ theme, setTheme, font, setFont, userRole, setUserRole, studentRequests, addStudentRequest, markRequestRead, resolveRequest, unreadRequestCount, adminThemes, setAdminThemes }}>
      {children}
    </AppContext.Provider>
  );
}

export function useAppContext() {
  return useContext(AppContext);
}
